import { useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { InstallerInformation } from "models/Users";
import { UserRole, getUserRoleName } from "features/auth/types/user";
import { Checklist } from "models/Checklist";
import { CheckCircleOutline, DeleteOutline } from "@mui/icons-material";
import { useGetInstallersQuery } from "app/api/users";
import { useEditFunctionCheckMutation } from "app/api/checklists";
import { toast } from "react-toastify";

type EditFunctionCheckProps = {
  checklist: Checklist;
  onClose: () => void;
};

const EditFunctionCheck: React.FC<EditFunctionCheckProps> = (props) => {
  const { checklist, onClose } = props;
  const { data: allInstallers } = useGetInstallersQuery();
  const [editFunctionCheck, { isLoading }] = useEditFunctionCheckMutation();

  const [selectedInstaller, setSelectedInstaller] = useState<InstallerInformation | null>(null);

  const [functionCheck, setFunctionCheck] = useState(checklist.functionCheck);
  const [functionCheckBy, setFunctionCheckBy] = useState(checklist.functionCheckBy);
  const [functionCheckAt, setFunctionCheckAt] = useState(checklist.functionCheckAt);
  const [functionCheckByAdmin, setFunctionCheckByAdmin] = useState(checklist.functionCheckByAdmin);

  const onClear = () => {
    setFunctionCheck(false);
    setFunctionCheckBy(null);
    setFunctionCheckAt(null);
    setFunctionCheckByAdmin(false);
  };

  const onSave = () => {
    editFunctionCheck({
      id: checklist.id,
      state: !!selectedInstaller,
      installerId: selectedInstaller?.userId,
    }).then(() => {
      toast.success("Funktionskontroll uppdaterad");
      onClose();
    });
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} fullWidth>
        <DialogTitle>Ändra status för funktionskontroll</DialogTitle>

        <DialogContent dividers>
          {functionCheck && (
            <>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight="bold">Funktionskontroll utförd</Typography>

                <CheckCircleOutline color="success" />
              </Box>

              <Box>
                <Typography fontSize="small">{functionCheckAt}</Typography>
                <Link
                  display="block"
                  fontSize="small"
                  component={RouterLink}
                  to={`/users/view/${functionCheckBy?.userId}`}
                >
                  {functionCheckBy?.userName}
                </Link>
                {functionCheckByAdmin && <Typography fontSize="small">Ändrad av admin</Typography>}
              </Box>

              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={onClear}
              >
                Ta bort
              </Button>
            </>
          )}

          {!functionCheck && (
            <>
              <DialogContentText>Sätt ny funktionskontroll</DialogContentText>

              <Autocomplete
                options={allInstallers || []}
                getOptionLabel={(option) => option.name}
                onChange={(_, value) => setSelectedInstaller(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Välj installatör"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: "off", // disable autocomplete and autofill
                      },
                    }}
                  />
                )}
                renderOption={(props, installer) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={installer.userId}
                      component="li"
                      {...optionProps}
                      display={"flex"}
                      alignItems="center"
                      gap={1}
                    >
                      <Typography>{installer.name}</Typography>

                      <Chip
                        size="small"
                        color={
                          installer.userRole === UserRole.INTERNAL_INSTALLER ? "success" : "info"
                        }
                        label={getUserRoleName(installer.userRole)}
                      />

                      {installer.userRole == UserRole.EXTERNAL_INSTALLER && (
                        <Chip size="small" color="secondary" label={installer.userCompany} />
                      )}
                    </Box>
                  );
                }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Avbryt
          </Button>
          <Button variant="contained" type="submit" onClick={onSave} loading={isLoading}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditFunctionCheck;
